import { gsap } from "./gsapSetup";

/**
 * Cursor badge that:
 * - follows the pointer with easing
 * - optionally tilts on movement (enabled below)
 * - wipes hover color left->right when hovering links/buttons/labels (mask animation)
 *
 * Markup required in HTML:
 * <div class="cursor-badge" aria-hidden="true"> ...svg below... </div>
 */
export function initCursorBadge() {
  const badge = document.querySelector(".cursor-badge");
  if (!badge) return;

  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (prefersReduced) return;

  const svg = badge.querySelector("svg");
  const wipeRect = badge.querySelector(".wipe-rect");
  if (!svg || !wipeRect) {
    // If you don't use the wipe SVG below, you can swap this logic to simple fill tweening.
    return;
  }

  // Cursor offset (bottom-right of pointer)
  const offset = { x: 2, y: 12 };

  // Make transforms predictable
  gsap.set(badge, {
    x: -9999,
    y: -9999,
    force3D: true,
    willChange: "transform",
  });

  // Prefer scaling the wrapper rather than <svg transform="scale(...)">
  gsap.set(badge, { scale: 0.8 });
  gsap.set(svg, { transformOrigin: "50% 50%" });

  // Smooth follow
  const xTo = gsap.quickTo(badge, "x", { duration: 0.35, ease: "power3.out" });
  const yTo = gsap.quickTo(badge, "y", { duration: 0.35, ease: "power3.out" });

  const onMove = (e) => {
    xTo(e.clientX + offset.x);
    yTo(e.clientY + offset.y);
  };

  window.addEventListener("pointermove", onMove, { passive: true });

  // --- Hover wipe (left -> right) ---
  const SVG_W = 35; // viewBox width (matches the provided SVG)
  gsap.set(wipeRect, { attr: { width: 0 } });

  const hoverTargets = document.querySelectorAll("a, button, label, [data-cursor-link]");

  const wipeTo = (open) => {
    gsap.to(wipeRect, {
      attr: { width: open ? SVG_W : 0 },
      duration: open ? 0.35 : 0.25,
      ease: "power2.out",
      overwrite: "auto",
    });
  };

  const onEnter = () => wipeTo(true);
  const onLeave = () => wipeTo(false);

  hoverTargets.forEach((el) => {
    el.addEventListener("pointerenter", onEnter);
    el.addEventListener("pointerleave", onLeave);
  });

  // cleanup (optional)
  return () => {
    window.removeEventListener("pointermove", onMove);
    hoverTargets.forEach((el) => {
      el.removeEventListener("pointerenter", onEnter);
      el.removeEventListener("pointerleave", onLeave);
    });
  };
}
